/**
 * JobInfoPanel Component
 *
 * Compact job information display with:
 * - Browser/Proxy/Model settings (configurable)
 * - Editable Goal and Website URL
 * - Parse + Run buttons in a row
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Job {
  job_id: string;
  goal?: string;
  website_url?: string;
  raw_input?: any;
  status: string;
  parsing_status?: string;
  parse_error?: string;
}

const MODELS = [
  { value: 'gemini-3-flash-preview', label: 'Gemini-3' },
  { value: 'supernova', label: 'Gemini-3-lite' },
  { value: 'cerebras/gpt-oss-120b', label: 'OSS' },
];

interface JobInfoPanelProps {
  job: Job;
  webAgentSessionId?: string | null;
  sessionId?: string | null;
  // Configurable settings
  browserType: string;
  useProxy: boolean;
  model: string;
  onBrowserTypeChange: (browserType: string) => void;
  onUseProxyChange: (useProxy: boolean) => void;
  onModelChange: (model: string) => void;
  // TinyContext settings
  tinyctxMode?: string;
  recordingId?: string;
  onTinyctxModeChange?: (mode: string) => void;
  onRecordingIdChange?: (id: string) => void;
  // Actions
  onRun: () => void;
  onStop: () => void;
  onSaveJob?: (data: { goal?: string; website_url?: string }) => Promise<void>;
  onDeleteJob?: () => void;
  isRunning: boolean;
  isStopping: boolean;
}

export const JobInfoPanel: React.FC<JobInfoPanelProps> = ({
  job,
  webAgentSessionId,
  sessionId,
  browserType,
  useProxy,
  model,
  onBrowserTypeChange,
  onUseProxyChange,
  onModelChange,
  tinyctxMode = 'live',
  recordingId = '',
  onTinyctxModeChange,
  onRecordingIdChange,
  onRun,
  onStop,
  onSaveJob,
  onDeleteJob,
  isRunning,
  isStopping,
}) => {
  const navigate = useNavigate();
  const disabled = isRunning;

  const [isEditing, setIsEditing] = useState(false);
  const [editGoal, setEditGoal] = useState(job.goal || '');
  const [editUrl, setEditUrl] = useState(job.website_url || '');
  const [isSaving, setIsSaving] = useState(false);

  // Sync edit fields when job data changes (e.g., after save)
  useEffect(() => {
    if (!isEditing) {
      setEditGoal(job.goal || '');
      setEditUrl(job.website_url || '');
    }
  }, [job.goal, job.website_url, isEditing]);

  const handleSave = async () => {
    if (!onSaveJob) return;
    setIsSaving(true);
    try {
      await onSaveJob({ goal: editGoal, website_url: editUrl });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditGoal(job.goal || '');
    setEditUrl(job.website_url || '');
    setIsEditing(false);
  };

  return (
    <div className="flex-shrink-0 px-4 py-3 space-y-3">
      {/* Settings Row - Browser, Proxy, Memory Bank (configurable) */}
      <div className="flex items-center gap-3 text-xs">
        {/* Browser Type Selector */}
        <select
          id="browser-type"
          value={browserType}
          onChange={(e) => onBrowserTypeChange(e.target.value)}
          disabled={disabled}
          className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
          aria-label="Browser type"
        >
          <option value="stealth">TinyFish</option>
          <option value="headless">TinyFish Light</option>
          <option value="kernel">Kernel</option>
          <option value="tetra">Tetra</option>
          <option value="tetra-anchor">Stealth</option>
        </select>

        {/* Proxy Toggle */}
        <div className="flex items-center gap-1.5">
          <button
            role="switch"
            aria-checked={useProxy}
            onClick={() => onUseProxyChange(!useProxy)}
            disabled={disabled}
            className={`
              relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed
              ${useProxy ? 'bg-blue-600' : 'bg-gray-300'}
            `}
            aria-label="Toggle proxy"
          >
            <span
              className={`
                inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform
                ${useProxy ? 'translate-x-3.5' : 'translate-x-0.5'}
              `}
            />
          </button>
          <span className="text-gray-600">Proxy</span>
        </div>

        {/* Model Selector */}
        <select
          id="model"
          value={model}
          onChange={(e) => onModelChange(e.target.value)}
          disabled={disabled}
          className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
          aria-label="Select model"
        >
          {MODELS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>

        {/* TinyContext Mode Selector */}
        {onTinyctxModeChange && (
          <select
            value={tinyctxMode}
            onChange={(e) => onTinyctxModeChange(e.target.value)}
            disabled={disabled}
            className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
            aria-label="TinyContext mode"
          >
            <option value="live">Live</option>
            <option value="record">Record</option>
            <option value="replay">Replay</option>
            <option value="hybrid">Hybrid</option>
          </select>
        )}

        {/* Recording ID input (shown for replay/hybrid) */}
        {onRecordingIdChange && (tinyctxMode === 'replay' || tinyctxMode === 'hybrid') && (
          <input
            type="text"
            value={recordingId}
            onChange={(e) => onRecordingIdChange(e.target.value)}
            placeholder="Recording ID (UUID)"
            disabled={disabled}
            className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white w-48"
            aria-label="Recording ID"
          />
        )}
      </div>

      {/* Job Information */}
      <div className="space-y-2 text-sm">
        {/* Goal */}
        <div>
          <dt className="text-xs font-medium text-gray-500 mb-1">Goal</dt>
          {isEditing ? (
            <textarea
              value={editGoal}
              onChange={(e) => setEditGoal(e.target.value)}
              rows={3}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 leading-tight"
            />
          ) : (
            <dd className="text-gray-900 break-all max-h-[6.75rem] overflow-y-auto text-sm leading-tight">
              {job.goal || <span className="text-gray-400 italic">Not set</span>}
            </dd>
          )}
        </div>

        {/* Website URL */}
        <div>
          <dt className="text-xs font-medium text-gray-500 mb-1">Website URL</dt>
          {isEditing ? (
            <input
              type="text"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          ) : (
            <dd className="text-gray-900">
              {job.website_url ? (
                <a
                  href={job.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 truncate block"
                  title={job.website_url}
                >
                  {job.website_url}
                </a>
              ) : (
                <span className="text-gray-400 italic">Not set</span>
              )}
            </dd>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {isEditing ? (
          <>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 focus:outline-none"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              onClick={isRunning ? onStop : onRun}
              disabled={isStopping}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md disabled:opacity-50 focus:outline-none ${
                isRunning
                  ? 'bg-white text-red-600 border-[0.5px] border-red-600 hover:bg-red-50'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
              }`}
            >
              {isStopping ? 'Stopping...' : isRunning ? 'Stop' : 'Run'}
            </button>
            {onSaveJob && !isRunning && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                title="Edit job goal and URL"
              >
                Edit
              </button>
            )}
            {onDeleteJob && !isRunning && (
              <button
                onClick={onDeleteJob}
                className="px-3 py-2 text-sm font-medium rounded-md bg-white text-red-600 border border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                title="Delete this job"
              >
                Delete
              </button>
            )}
            {sessionId && (
              <button
                onClick={() => navigate(`/checkpoint/${sessionId}`)}
                className="px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                title="Replay session with checkpoint restore"
              >
                Replay
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
